import React from 'react';
import ChatMessageBubble from '../../ChatMessageBubble';
import FileIcon from './FileIcon';
import Preview from './Preview';
import MessageTooltip from '../../MessageTooltip';

interface ChatRenderAttachmentProps {
  message: {
    attachment: {
      id: string;
      filename: string;
      mimetype: string;
      url: string;
    };
    createdAt: number;
  };
  children?: React.ReactNode;
}

interface AttachmentRenderProps {
  address: string;
  filename: string;
}

const renders = {
  image: ({ address, filename }: AttachmentRenderProps) => (
    <a
      className="chat-message-inline-picture-zoom-in"
      target="_blank"
      href={address}
      rel="noopener noreferrer"
    >
      <img alt={filename} src={address} />
    </a>
  ),
  audio: ({ address }: Pick<AttachmentRenderProps, 'address'>) => (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <audio controls>
      <source src={address} type="audio/mpeg" />
      Your browser does not support the <code>audio</code> element.
      You can <a href={address}>download the audio</a> instead.
    </audio>
  ),
  video: ({ address }: Pick<AttachmentRenderProps, 'address'>) => (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video controls>
      <source src={address} type="video/mp4" />
      Your browser does not support the <code>video</code> element.
      You can <a href={address}>download the video</a> instead.
    </video>
  ),
  fallback: ({ address, filename }: AttachmentRenderProps) => (
    <div className="chat-message-text-message">
      <a target="_blank" href={address} rel="noopener noreferrer">
        <FileIcon />{filename || 'file'}
      </a>
    </div>
  ),
};

const ChatRenderAttachment = function ({
  message,
  children = null,
}: ChatRenderAttachmentProps) {
  const { attachment } = message;
  const [type] = attachment.mimetype?.split('/') || [];
  const render = renders[type as keyof typeof renders] || renders.fallback;
  const isMedia = ['video', 'image', 'audio'].includes(type);
  return (
    <MessageTooltip message={message}>
      <ChatMessageBubble borderless={isMedia} transparent={isMedia}>
        <Preview>
          {render({
            address: encodeURI(attachment.url),
            filename: attachment.filename,
          })}
        </Preview>
        {children}
      </ChatMessageBubble>
    </MessageTooltip>
  );
};

export default ChatRenderAttachment;
